import { useEffect, useState } from 'react';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';

/** Membership in this group marks a user as external (see org/external-users.yaml). */
export const EXTERNAL_GROUP_REF = 'group:default/external';

// Cached after sign-in by ExternalUserProbe (mounted at the app root), so
// synchronous contexts - entity tab filters in particular, which receive only
// the entity - can also tell external users apart. Everything hidden this way
// is ALSO enforced server-side (permission policy + the techdocs/github
// guards); this cache is purely cosmetic.
let cachedIsExternal: boolean | undefined;

/** Sync read of the cached value; false until the probe has resolved. */
export const isExternalUserSync = () => cachedIsExternal ?? false;

/** Whether the signed-in user is an external partner (undefined while loading). */
export function useIsExternalUser(): boolean | undefined {
  const identityApi = useApi(identityApiRef);
  const [value, setValue] = useState(cachedIsExternal);
  useEffect(() => {
    let mounted = true;
    identityApi
      .getBackstageIdentity()
      .then(identity => {
        const external =
          identity.ownershipEntityRefs.includes(EXTERNAL_GROUP_REF);
        cachedIsExternal = external;
        if (mounted) {
          setValue(external);
        }
      })
      .catch(() => {
        if (mounted) {
          setValue(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, [identityApi]);
  return value;
}

/** Warms the cache right after sign-in; renders nothing. */
export function ExternalUserProbe() {
  useIsExternalUser();
  return null;
}
